import type { Cell, EditorBlock, Node, Row, Section } from '@getaccept/editor-lib-new';
import type { Comment } from '@getaccept/lib-shared-new/src/contextual-commenting/types/comment';
import type { DocumentPage } from '../../types/document-page';

export class FloatingCommentsHelper {
  static getEditorBlocksFromPages(pages: DocumentPage[]) {
    return pages
      .filter((page: DocumentPage) => !!page.editorBlock)
      .map((page: DocumentPage) => page.editorBlock);
  }

  static getComments(comments: Comment[], editorBlocks: EditorBlock[]) {
    const editorBlockIds = editorBlocks.map((editorBlock: EditorBlock) => editorBlock.id);

    const sections: Section[] = editorBlocks.flatMap(
      (editorBlock: EditorBlock) => editorBlock.content.sections
    );
    const sectionIds: string[] = sections.map((section: Section) => section.id);

    const rows: Row[] = sections.flatMap((section: Section) => section.rows);
    const rowIds: string[] = rows.map((row: Row) => row.id);

    const cells: Cell[] = rows.flatMap((row: Row) => row.cells);
    const cellIds: string[] = cells.map((cell: Cell) => cell.id);

    const nodes: Node[] = cells.flatMap((cell: Cell) => cell.nodes);
    const nodeIds: string[] = nodes.map((node: Node) => node.id);

    return [...comments]
      .sort(FloatingCommentsHelper.sortCommentsByEditorTextSelection)
      .sort((a, b) => FloatingCommentsHelper.sortCommentByIdOrder(a, b, nodeIds, 'nodeId'))
      .sort((a, b) => FloatingCommentsHelper.sortCommentByIdOrder(a, b, cellIds, 'cellId'))
      .sort((a, b) => FloatingCommentsHelper.sortCommentByIdOrder(a, b, rowIds, 'rowId'))
      .sort((a, b) => FloatingCommentsHelper.sortCommentByIdOrder(a, b, sectionIds, 'sectionId'))
      .sort((a, b) =>
        FloatingCommentsHelper.sortCommentByIdOrder(a, b, editorBlockIds, 'editorBlockId')
      );
  }

  static sortCommentByIdOrder(
    a: Comment,
    b: Comment,
    ids: string[],
    idProperty: 'editorBlockId' | 'sectionId' | 'rowId' | 'cellId' | 'nodeId'
  ): number {
    const aPagePosition = ids.findIndex(id => id === a.editorSelection[idProperty]);
    if (aPagePosition === -1) {
      return -1;
    }

    const bPagePosition = ids.findIndex(id => id === b.editorSelection[idProperty]);
    if (aPagePosition < bPagePosition) {
      return -1;
    }
    if (aPagePosition > bPagePosition) {
      return 1;
    }
    return 0;
  }

  static sortCommentsByEditorTextSelection(a: Comment, b: Comment): number {
    if (!a.editorSelection.textSelection) {
      return -1;
    }
    if (!b.editorSelection.textSelection) {
      return 1;
    }
    const aTextSelection = a.editorSelection.textSelection;
    const aFrom =
      aTextSelection.anchor < aTextSelection.head ? aTextSelection.anchor : aTextSelection.head;
    const bTextSelection = b.editorSelection.textSelection;
    const bFrom =
      bTextSelection.anchor < bTextSelection.head ? bTextSelection.anchor : bTextSelection.head;
    return aFrom - bFrom;
  }

  static getMarginToRemoveFromComment(
    commentMargin: number,
    marginToRemove: number,
    minimumMargin: number
  ): number {
    const diff = commentMargin - marginToRemove;
    if (diff >= minimumMargin) {
      return marginToRemove;
    }
    return commentMargin - minimumMargin;
  }

  static getReversedCommentIdsBeforeId(id: string, comments: Comment[]): string[] {
    const index = comments.findIndex((comment: Comment) => comment.id === id);
    return comments
      .map((comment: Comment) => comment.id)
      .slice(0, index)
      .reverse();
  }

  static getCommentIdsAfterSelectedComment(id: string, comments: Comment[]): string[] {
    const index = comments.findIndex((comment: Comment) => comment.id === id);
    return comments.map((comment: Comment) => comment.id).slice(index + 1);
  }

  static willCommentBePushedDown(futureDistance: number, desiredDistance: number): boolean {
    return futureDistance > desiredDistance;
  }

  static canDecreaseMargin(selectionMargin: number, minimumMargin: number): boolean {
    return selectionMargin > minimumMargin;
  }

  static decreaseMargin(
    selectionMargin: number,
    desiredDistance: number,
    futureDistance: number,
    minimumMargin: number
  ): number {
    const distanceFromDesired = futureDistance - desiredDistance;
    const resultingMargin = selectionMargin - distanceFromDesired;
    if (resultingMargin < minimumMargin) {
      return minimumMargin;
    }
    return resultingMargin;
  }

  static idsUnchanged(newComments: Comment[], oldComments: Comment[]): boolean {
    const newIds = newComments.map((comment: Comment) => comment.id);
    const oldIds = oldComments.map((comment: Comment) => comment.id);
    return newIds.every((newId: string) => oldIds.includes(newId));
  }
}
